import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/auth/PasswordSetupView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/accept-invite',
    name: 'accept-invite',
    component: () => import('@/views/auth/PasswordSetupView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/family',
    component: () => import('@/layouts/FamilyLayout.vue'),
    meta: { requiresAuth: true, roles: ['FAMILY', 'ADMIN'] },
    children: [
      {
        path: '',
        name: 'family-home',
        component: () => import('@/views/family/FamilyHomeView.vue'),
        meta: { title: 'Início' },
      },
      {
        path: 'children/:childId',
        name: 'family-child',
        component: () => import('@/views/family/FamilyChildView.vue'),
        meta: { title: 'Meu Filho' },
      },
      {
        path: 'activities',
        name: 'family-home-activities',
        component: () => import('@/views/family/FamilyHomeActivitiesView.vue'),
        meta: { title: 'Atividades para Casa' },
      },
      {
        path: 'timeline',
        name: 'family-timeline',
        component: () => import('@/views/family/FamilyTimelineView.vue'),
        meta: { title: 'Conquistas' },
      },
      {
        path: 'profile',
        name: 'family-profile',
        component: () => import('@/views/family/FamilyProfileView.vue'),
        meta: { title: 'Meu Perfil' },
      },
    ],
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, roles: ['ADMIN', 'SUPERVISOR', 'THERAPIST', 'ASSISTANT'] },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { title: 'Dashboard' },
      },
      {
        path: 'children',
        name: 'children',
        component: () => import('@/views/children/ChildrenListView.vue'),
        meta: { title: 'Crianças' },
      },
      {
        path: 'children/:id',
        name: 'child-profile',
        component: () => import('@/views/children/ChildProfileView.vue'),
        meta: { title: 'Perfil da Criança' },
      },
      {
        path: 'children/:id/edit',
        name: 'child-edit',
        component: () => import('@/views/children/ChildEditView.vue'),
        meta: { title: 'Editar Criança', roles: ['ADMIN', 'SUPERVISOR', 'THERAPIST'] },
      },
      {
        path: 'children/:childId/assessment/new',
        name: 'assessment-new',
        component: () => import('@/views/assessments/AssessmentFormView.vue'),
        meta: { title: 'Nova Avaliação' },
      },
      {
        path: 'children/:childId/assessment/:id',
        name: 'assessment-edit',
        component: () => import('@/views/assessments/AssessmentFormView.vue'),
        meta: { title: 'Editar Avaliação' },
      },
      {
        path: 'children/:childId/assessment/:assessmentId/compare',
        name: 'assessment-compare',
        component: () => import('@/views/assessments/AssessmentCompareView.vue'),
        meta: { title: 'Comparar Avaliações' },
      },
      {
        path: 'assessments',
        name: 'assessments',
        component: () => import('@/views/assessments/AssessmentsIndexView.vue'),
        meta: { title: 'Avaliações' },
      },
      {
        path: 'plans',
        name: 'plans',
        component: () => import('@/views/plans/PlansIndexView.vue'),
        meta: { title: 'Planos' },
      },
      {
        path: 'reports/progress',
        name: 'reports-progress',
        component: () => import('@/views/reports/ProgressReportView.vue'),
        meta: { title: 'Progresso' },
      },
      {
        path: 'reports/stats',
        name: 'reports-stats',
        component: () => import('@/views/reports/StatsReportView.vue'),
        meta: { title: 'Estatísticas' },
      },
      {
        path: 'reports/evolution/:childId?',
        name: 'reports-evolution',
        component: () => import('@/views/reports/EvolutionReportView.vue'),
        meta: { title: 'Evolução Longitudinal' },
      },
      {
        path: 'reports/family/:childId',
        name: 'reports-family',
        component: () => import('@/views/reports/FamilyReportView.vue'),
        meta: { title: 'Relatório para a Família' },
      },
      {
        path: 'alerts',
        name: 'alerts',
        component: () => import('@/views/alerts/AlertsView.vue'),
        meta: { title: 'Alertas Clínicos' },
      },
      {
        path: 'schedule',
        name: 'schedule',
        component: () => import('@/views/schedule/ScheduleView.vue'),
        meta: { title: 'Agenda' },
      },
      {
        path: 'activities',
        name: 'activities',
        component: () => import('@/views/activities/ActivityLibraryView.vue'),
        meta: { title: 'Biblioteca de Atividades' },
      },
      {
        path: 'supervision',
        name: 'supervision',
        component: () => import('@/views/supervision/SupervisionListView.vue'),
        meta: { title: 'Supervisão' },
      },
      {
        path: 'supervision/new',
        name: 'supervision-new',
        component: () => import('@/views/supervision/SupervisionMeetingFormView.vue'),
        meta: { title: 'Novo Encontro de Supervisão', roles: ['ADMIN', 'SUPERVISOR'] },
      },
      {
        path: 'supervision/:id/edit',
        name: 'supervision-edit',
        component: () => import('@/views/supervision/SupervisionMeetingFormView.vue'),
        meta: { title: 'Editar Encontro de Supervisão', roles: ['ADMIN', 'SUPERVISOR'] },
      },
      {
        path: 'supervision/:id',
        name: 'supervision-detail',
        component: () => import('@/views/supervision/SupervisionMinutesView.vue'),
        meta: { title: 'Ata de Supervisão' },
      },
      {
        path: 'sessions/plan',
        name: 'session-planner',
        component: () => import('@/views/sessions/SessionPlannerView.vue'),
        meta: { title: 'Planejar Sessão' },
      },
      {
        path: 'performance',
        name: 'performance',
        component: () => import('@/views/performance/MyPerformanceView.vue'),
        meta: { title: 'Meu Desempenho' },
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/views/admin/UsersView.vue'),
        meta: { title: 'Usuários', roles: ['ADMIN'] },
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/admin/SettingsView.vue'),
        meta: { title: 'Configurações', roles: ['ADMIN'] },
      },
      {
        path: 'audit',
        name: 'audit',
        component: () => import('@/views/admin/AuditLogView.vue'),
        meta: { title: 'Auditoria', roles: ['ADMIN'] },
      },
      {
        path: 'ai-interactions',
        name: 'ai-interactions',
        component: () => import('@/views/admin/AiInteractionsView.vue'),
        meta: { title: 'Interações com IA', roles: ['ADMIN'] },
      },
      {
        path: 'consents-admin',
        name: 'consents-admin',
        component: () => import('@/views/admin/ConsentsView.vue'),
        meta: { title: 'Consentimentos', roles: ['ADMIN'] },
      },
      {
        path: 'admin/backup-import',
        name: 'backup-import',
        component: () => import('@/views/admin/BackupImportView.vue'),
        meta: { title: 'Importar Backup', roles: ['ADMIN'] },
      },
      {
        path: 'admin/instruments',
        name: 'instruments-admin',
        component: () => import('@/views/admin/InstrumentsListView.vue'),
        meta: { title: 'Instrumentos', roles: ['ADMIN'] },
      },
      {
        path: 'admin/instruments/:id',
        name: 'instrument-editor',
        component: () => import('@/views/admin/InstrumentEditorView.vue'),
        meta: { title: 'Editor de Instrumento', roles: ['ADMIN'] },
      },
      {
        path: 'children/:childId/instrument/:instrumentId/assessment/:assessmentId?',
        name: 'generic-assessment',
        component: () => import('@/views/assessments/GenericAssessmentView.vue'),
        meta: { title: 'Avaliação' },
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/ProfileView.vue'),
        meta: { title: 'Meu Perfil' },
      },
      {
        path: 'help',
        name: 'help',
        component: () => import('@/views/HelpView.vue'),
        meta: { title: 'Ajuda' },
      },
      {
        path: 'children/:childId/plans/new',
        name: 'plan-new',
        component: () => import('@/views/plans/PlanCreationView.vue'),
        meta: { title: 'Novo Plano Terapêutico' },
      },
      {
        path: 'children/:childId/plans/:id',
        name: 'plan-edit',
        component: () => import('@/views/plans/PlanEditView.vue'),
        meta: { title: 'Editar Plano Terapêutico' },
      },
      {
        path: 'plans/:planId/tracking',
        name: 'plan-tracking',
        component: () => import('@/views/plans/PlanTrackingView.vue'),
        meta: { title: 'Acompanhamento do Plano' },
      },
      {
        path: 'sessions/collect',
        name: 'data-collection',
        component: () => import('@/views/sessions/DataCollectionView.vue'),
        meta: { title: 'Coleta de Dados' },
      },
      {
        path: 'children/:id/progress',
        name: 'child-progress',
        component: () => import('@/views/children/ProgressView.vue'),
        meta: { title: 'Progresso' },
      },
      // {
      //   path: 'children/:childId/collection/new',
      //   name: 'collection-new',
      //   component: () => import('@/views/collections/CollectionFormView.vue'),
      // },
      // {
      //   path: 'profile',
      //   name: 'profile',
      //   component: () => import('@/views/ProfileView.vue'),
      // },
    ],
  },
  // {
  //   path: '/:pathMatch(.*)*',
  //   name: 'not-found',
  //   component: () => import('@/views/NotFoundView.vue'),
  // },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

/** Rota inicial de cada perfil após o login. */
export function homeRouteFor(role?: string | null) {
  if (role === 'FAMILY' && router.hasRoute('family-home')) {
    return { name: 'family-home' };
  }
  return { name: 'dashboard' };
}

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const requiresAuth = to.meta.requiresAuth !== false;
  const isAuthenticated = authStore.isAuthenticated;

  if (requiresAuth && !isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } });
    return;
  }

  if (!requiresAuth && isAuthenticated && to.name === 'login') {
    next(homeRouteFor(authStore.user?.role));
    return;
  }

  // Role-based guard: routes without meta.roles stay open to any authenticated
  // user. When parent AND child define roles, the user must satisfy BOTH
  // (child narrows the parent — never widens it).
  if (isAuthenticated) {
    const roleRestrictions = to.matched
      .map((record) => record.meta.roles as string[] | undefined)
      .filter((roles): roles is string[] => Array.isArray(roles) && roles.length > 0);

    if (roleRestrictions.length > 0) {
      // On hard navigation the user profile may not be loaded yet — wait for it,
      // otherwise the role check would be silently skipped.
      if (!authStore.user) {
        try {
          await authStore.fetchCurrentUser();
        } catch {
          // token inválido: interceptors/logout cuidam do redirecionamento
        }
      }
      const role = authStore.user?.role;
      if (!role || !roleRestrictions.every((roles) => roles.includes(role))) {
        next(homeRouteFor(role));
        return;
      }
    }
  }

  next();
});

export default router;
